import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ImportButton, { ImportButtonProps } from './ImportButton';
import ImportDropzone, { AllowedFileTypes, ImportDropzoneProps } from './ImportDropzone';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';
import { useAuth } from 'react-oidc-context';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { useMutation } from '@tanstack/react-query';
import { UploadModel, postImportFileUpload } from 'src/services/import';
import { useAtom } from 'src/@core/hooks/useVigoAtomStore';
import { importFormMethodsAtom } from './importAtomStore';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch } from 'src/store/baseHooks';
import { addPanel, removePanel } from 'src/store/slices/uiFlowSlice';

const defaultFormState: { templateId: string; customerId: string; file: File | null; useAccountFromFile: boolean } = {
  useAccountFromFile: false,
  customerId: '',
  templateId: '',
  file: null
};

const flowSchema = yup.object().shape({
  useAccountFromFile: yup.boolean(),
  customerId: yup
    .string()
    .typeError('Customer is required')
    .when('useAccountFromFile', {
      is: false,
      then: yup.string().typeError('Customer is required').required('Customer is required')
    }),
  templateId: yup.string().typeError('Import template is required').required('Import template is required'),
  file: yup.mixed().required('A source file is required')
});

const myPortalSchema = yup.object().shape({
  useAccountFromFile: yup.boolean(),
  customerId: yup.string().optional(),
  templateId: yup.string().typeError('Import template is required').required('Import template is required'),
  file: yup.mixed().required('A source file is required')
});

const customUploadSchema = yup.object().shape({
  useAccountFromFile: yup.boolean(),
  customerId: yup.string().optional(),
  templateId: yup.string().optional(),
  file: yup.mixed().required('A source file is required')
});

export type DatasourceType = 'Address' | 'Consignment';

interface useImportToolsProps {
  dtoId?: string;
  dropZoneDisabled?: boolean;
  buttonTooltipText?: string;
  datasourceName?: string;
  allowedExtensions?: AllowedFileTypes[];
  confirmationMessage?: string;
  customUploadMutation?: (file: File, customerId?: string) => Promise<any>;
}

const useImportTools = ({
  dtoId,
  dropZoneDisabled = false,
  buttonTooltipText = 'Import',
  datasourceName = 'Consignment',
  allowedExtensions = ['CSV'],
  confirmationMessage,
  customUploadMutation
}: useImportToolsProps) => {
  const { vigoToast } = useVigoToast({});
  const auth = useAuth();
  const prod = useGetProductLevel();
  const dispatch = useAppDispatch();

  // Use custom schema if custom upload provided (doesnt require customer)
  const getSchema = () => {
    if (customUploadMutation) return customUploadSchema;
    
    return prod === Products.MyPortal ? myPortalSchema : flowSchema;
  };

  const methods = useForm({
    defaultValues: defaultFormState,
    resolver: yupResolver(getSchema())
  });

  const { setValue, handleSubmit, reset } = methods;

  const importButtonRef = useRef<HTMLButtonElement>(null);

  const [, setImportFormMethodsAtom] = useAtom(importFormMethodsAtom);

  const useAccFromFile = useWatch({ control: methods.control, name: 'useAccountFromFile', defaultValue: false });

  /**
   * Retrieves the partner ID for the logged-in user.
   * If the user is impersonating a tenant, it returns the partner ID from the session storage.
   * Otherwise, it returns the partner ID from the user's profile.
   * @returns The partner ID as a string.
   */
  const getPartnerId = useCallback(() => {
    const user = auth.user;
    const loggedInUserId = user?.profile.tenantUserId as string;

    // check if we are already impersonating a tenant
    const fromSession = window.sessionStorage.getItem(`impersonate-${loggedInUserId}`);
    if (fromSession) {
      return JSON.parse(fromSession).partnerId ?? '';
    }

    return (user?.profile?.partnerId as string) ?? '';
  }, [auth]);

  const uploadMutation = useMutation({
    mutationKey: ['importFileUploadMutation'],
    mutationFn: async (formData: UploadModel) => {
      // If custom upload mutation provided, use it instead
      if (customUploadMutation) {
        return customUploadMutation(formData.file as File, formData.customerId);
      }

      if (prod === Products.Flow && useAccFromFile) {
        formData.customerId = getPartnerId();
      }

      return postImportFileUpload({ ...formData });
    }
  });

  const handleImport = useCallback((onSuccess?: () => void) => {
    handleSubmit(
      formData => {
        return vigoToast.promise(
          'Submitting Import ...',
          'Import Has Been Submitted',
          'Error Trying To Import',
          true,
          uploadMutation
            .mutateAsync({
              templateId: formData.templateId,
              file: formData.file as File,
              customerId: formData.customerId
            })
            .then(() => {
              resetFormState();
              onSuccess?.();
            })
        );
      },
      errors => {
        vigoToast.error('One or more validation errors occurred');
        console.log('Validation Errors', errors);
      }
    )();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Resets the form state.
   */
  const resetFormState = useCallback(() => {
    const state = structuredClone(defaultFormState);

    /**
     * If the product is Connect, set the customer ID to the partner ID.
     */
    if (prod === Products.MyPortal) {
      state.customerId = getPartnerId();
    }

    reset(state);
  }, [prod, reset, getPartnerId]);

  /**
   * Handles the accept action.
   */
  const accept = useCallback(
    (closePanel: () => void) => {
      handleImport(closePanel);
    },
    [handleImport]
  );

  /**
   * Cancels the import process.
   */
  const cancel = useCallback(() => {
    resetFormState();
  }, [resetFormState]);

  /**
   * Handles the file drop event.
   *
   * @param files - The array of files dropped.
   */
  const handleFileDrop = useCallback((files: File[]) => {
    setValue('file', files[0]);

    if (files?.length > 0) {
      importButtonRef.current?.click();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ImportButtonWithProps = useMemo(
    () => (props: ImportButtonProps) =>
      (
        <FormProvider {...methods}>
          <ImportButton
            {...props}
            onAccept={accept}
            onCancel={cancel}
            dtoId={dtoId}
            ref={importButtonRef}
            toolTipText={buttonTooltipText}
            confirmationMessage={confirmationMessage}
          />
        </FormProvider>
      ),
    [accept, buttonTooltipText, cancel, dtoId, methods, confirmationMessage]
  );

  const ImportDropzoneWithProps = useMemo(
    () => (props: ImportDropzoneProps) =>
      (
        <FormProvider {...methods}>
          <ImportDropzone
            {...props}
            onFileDropped={handleFileDrop}
            disabled={dropZoneDisabled}
            allowedFileTypes={allowedExtensions}
          />
        </FormProvider>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods, handleFileDrop, dtoId, dropZoneDisabled]
  );

  useEffect(() => {
    /**
     * Resets the form state when the product, auth or dtoId changes.
     */
    resetFormState();

    return () => {
      resetFormState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prod, auth, dtoId]);

  useEffect(() => {
    /** Register the import panel */
    dispatch(
      addPanel({
        id: `importPanel-${dtoId}`,
        title: `importPanel-${dtoId}`,
        componentName: `${datasourceName}ImportPanel`,
        open: false,
        fixed: false
      })
    );

    return () => {
      dispatch(removePanel(`importPanel-${dtoId}`));
      setImportFormMethodsAtom({});
      resetFormState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dtoId]);

  useEffect(() => {
    setImportFormMethodsAtom(methods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods, dtoId]);

  const returnObject = useMemo(
    () => ({
      ImportButton: ImportButtonWithProps,
      ImportDropzone: ImportDropzoneWithProps
    }),
    [ImportButtonWithProps, ImportDropzoneWithProps]
  );

  return returnObject;
};

export default useImportTools;
